import React, { useState } from 'react';
import { MapPin, Phone, Mail } from 'lucide-react';
import { mockData } from '../mock';
import { useToast } from '../hooks/use-toast';
import SEO from '../components/SEO';

const Contact = () => {
  const { doula } = mockData;
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    dueDate: '',
    message: '',
    formType: 'contact'
  });

  const [consultationData, setConsultationData] = useState({
    name: '',
    email: '',
    phone: '',
    dueDate: '',
    services: [],
    hearAbout: '',
    additionalInfo: ''
  });

  const [activeForm, setActiveForm] = useState('contact');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (activeForm === 'contact') {
      setFormData({ ...formData, [name]: value });
    } else {
      setConsultationData({ ...consultationData, [name]: value });
    }
  };

  const handleServiceToggle = (service) => {
    const services = consultationData.services.includes(service)
      ? consultationData.services.filter(s => s !== service)
      : [...consultationData.services, service];
    setConsultationData({ ...consultationData, services });
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    // Mock submission
    console.log('Contact form submitted:', formData);
    toast({
      title: "Message Sent!",
      description: "Thank you for reaching out. I'll respond within 24 hours.",
    });
    setFormData({ name: '', email: '', phone: '', dueDate: '', message: '', formType: 'contact' });
  };

  const handleConsultationSubmit = (e) => {
    e.preventDefault();
    // Mock submission
    console.log('Consultation form submitted:', consultationData);
    toast({
      title: "Consultation Request Received!",
      description: "I'll reach out within 24 hours to schedule our conversation.",
    });
    setConsultationData({ name: '', email: '', phone: '', dueDate: '', services: [], hearAbout: '', additionalInfo: '' });
  };

  const serviceOptions = ['Prenatal Support', 'Labor Support', 'Postpartum Care', 'Herbal Consultations', 'Tea Blends'];

  return (
    <div className="contact-page">
      <SEO 
        title="Contact 12 Tribes Doula Services | Schedule Free Consultation in Central Georgia"
        description="Ready to begin your supported birth journey? Contact us at (678) 876-1637 or info@12tribesdoula.com to schedule a free consultation. Serving Central Georgia."
        keywords="contact doula Central Georgia, schedule doula consultation, birth support Macon, doula phone number Georgia, free doula consultation"
        url="https://georgia-birth-coach.preview.emergentagent.com/contact"
      />

      {/* Hero Section */}
      <section className="page-hero">
        <div className="container">
          <h1 className="heading-hero">Let's Connect</h1>
          <p className="body-large" style={{maxWidth: '700px', margin: '0 auto'}}>Ready to begin your supported birth journey? Reach out to schedule a consultation</p>
        </div>
      </section>

      {/* Contact Info */}
      <section className="contact-info-section">
        <div className="container">
          <div className="contact-info-grid">
            <div className="contact-info-card">
              <MapPin size={24} />
              <h3 className="heading-3">Location</h3>
              <p className="body-small">{doula.location}</p>
            </div>
            <div className="contact-info-card">
              <Phone size={24} />
              <h3 className="heading-3">Phone</h3>
              <p className="body-small">{doula.phone}</p>
            </div>
            <div className="contact-info-card">
              <Mail size={24} />
              <h3 className="heading-3">Email</h3>
              <p className="body-small">{doula.email}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Forms Section */}
      <section className="forms-section">
        <div className="container">
          {/* Form Toggle */}
          <div className="form-toggle">
            <button 
              className={`btn-tag ${activeForm === 'contact' ? 'active' : ''}`}
              onClick={() => setActiveForm('contact')}
            >
              General Contact
            </button>
            <button 
              className={`btn-tag ${activeForm === 'consultation' ? 'active' : ''}`}
              onClick={() => setActiveForm('consultation')}
            >
              Book Consultation
            </button>
          </div>

          {/* Contact Form */}
          {activeForm === 'contact' && (
            <form onSubmit={handleContactSubmit} className="contact-form">
              <h2 className="heading-2">Send a Message</h2>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Due Date (if applicable)</label>
                  <input
                    type="date"
                    name="dueDate"
                    value={formData.dueDate}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Message *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  className="form-textarea"
                  rows="5"
                  required
                ></textarea>
              </div>
              <button type="submit" className="btn-primary">Send Message</button>
            </form>
          )}

          {/* Consultation Form */}
          {activeForm === 'consultation' && (
            <form onSubmit={handleConsultationSubmit} className="contact-form">
              <h2 className="heading-2">Request a Consultation</h2>
              <p className="body-small" style={{marginBottom: '2rem', color: 'var(--text-muted)'}}>Free initial consultation to discuss your needs and how I can support you</p>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={consultationData.name}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={consultationData.email}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Phone *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={consultationData.phone}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Expected Due Date *</label>
                  <input
                    type="date"
                    name="dueDate"
                    value={consultationData.dueDate}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Services Interested In *</label>
                <div className="service-checkboxes">
                  {serviceOptions.map((service) => (
                    <label key={service} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={consultationData.services.includes(service)}
                        onChange={() => handleServiceToggle(service)}
                        className="checkbox-input"
                      />
                      <span className="body-small">{service}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">How did you hear about me?</label>
                <input
                  type="text"
                  name="hearAbout"
                  value={consultationData.hearAbout}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Friend, social media, search, etc."
                />
              </div>

              <div className="form-group">
                <label className="form-label">Tell me about your birth vision</label>
                <textarea
                  name="additionalInfo"
                  value={consultationData.additionalInfo}
                  onChange={handleInputChange}
                  className="form-textarea"
                  rows="5"
                  placeholder="Share any specific needs, concerns, or questions you have..."
                ></textarea>
              </div>

              <button type="submit" className="btn-primary">Request Consultation</button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default Contact;
